import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { waitlistApi } from "../lib/waitlistApi";
import { useSalon } from "../context/SalonContext";

const PERIOD_LABEL = { manha: "Manhã", tarde: "Tarde", noite: "Noite", any: "Qualquer" };

const fmtDayTime = (ts) => {
    try {
        const d = new Date(ts);
        const day = d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" });
        const hm = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
        return `${day} às ${hm}`;
    } catch { return ""; }
};

const relativeSince = (iso) => {
    if (!iso) return "";
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return "agora";
        if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
        return `${Math.floor(diff / 86400)}d atrás`;
    } catch { return ""; }
};

/**
 * Lista de espera unificada (Agenda).
 *
 * Mostra num lugar só TODOS os agendamentos ainda não confirmados:
 *  - "match"   → já tem dia/hora (appointment status awaiting_confirmation,
 *                vindo do link público). Borda/glow VERDE. Botões
 *                Confirmar/Recusar — mesma ação do card "AGUARDA" da Agenda.
 *  - "nomatch" → cliente na fila sem horário definido (coleção waitlist).
 *                Borda/glow VERMELHA. Botão "Definir horário" abre o seletor
 *                de slot na Agenda; escolher o slot já efetiva a confirmação.
 */
const WaitlistDrawer = ({
    pendingAppointments = [],
    onConfirmAppointment,
    onRefuseAppointment,
    onDefineTime,
    onDragStart,
    onDragEnd,
    refreshTick,
    onClose,
}) => {
    const { clients, services } = useSalon();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { setEntries(await waitlistApi.list("all")); }
        catch (e) { toast.error("Falha ao carregar lista", { description: e.message }); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load, refreshTick]);

    const serviceNamesOf = useCallback((ids = []) => {
        if (!ids || !ids.length) return "";
        return ids.map((id) => services.find((s) => s.id === id)?.name).filter(Boolean).join(" + ");
    }, [services]);

    // ── Lista unificada: match (agendamento aguardando) + sem match (fila) ──
    const items = useMemo(() => {
        const matched = pendingAppointments.map((a) => ({
            type: "match",
            key: `appt-${a.id}`,
            id: a.id,
            clientName: a.clientName || a.client_name_snapshot || "Cliente",
            clientPhone: a.clientPhone || a.client_phone_snapshot || "",
            serviceNames: a.serviceName || serviceNamesOf(a.serviceIds || a.service_ids) || "—",
            whenLabel: fmtDayTime(a.date),
            sortKey: typeof a.date === "number" ? a.date : Date.parse(a.date) || 0,
            raw: a,
        }));
        const queue = entries
            .filter((e) => e.status === "waiting" || e.status === "notified")
            .map((e) => {
                const offered = e.offered_date && e.offered_time
                    ? `${e.offered_date} às ${e.offered_time}` : null;
                return {
                    type: "nomatch",
                    key: `wl-${e.id}`,
                    id: e.id,
                    clientName: e.client_name,
                    clientPhone: e.client_phone,
                    serviceNames: e.service_names || serviceNamesOf(e.service_ids) || "—",
                    period: PERIOD_LABEL[e.preferred_period] || "Qualquer",
                    whenLabel: offered ? `Ofertado: ${offered} · aguardando cliente` : "Sem horário definido",
                    since: relativeSince(e.created_at),
                    notified: e.status === "notified",
                    remaining: e.remaining_minutes,
                    sortKey: Number.MAX_SAFE_INTEGER,
                    raw: e,
                };
            });
        return [...matched, ...queue].sort((a, b) => a.sortKey - b.sortKey);
    }, [pendingAppointments, entries, serviceNamesOf]);

    const matchedCount = items.filter((i) => i.type === "match").length;
    const queueCount = items.length - matchedCount;

    const handleConfirm = async (item) => {
        setBusyId(item.id);
        try { await onConfirmAppointment?.(item.raw); }
        finally { setBusyId(null); }
    };
    const handleRefuseMatched = async (item) => {
        if (!window.confirm(`Recusar o agendamento de ${item.clientName}? O cliente será avisado por WhatsApp.`)) return;
        setBusyId(item.id);
        try { await onRefuseAppointment?.(item.raw); }
        finally { setBusyId(null); }
    };
    const handleRemoveQueue = async (item) => {
        if (!window.confirm(`Remover ${item.clientName} da lista de espera?`)) return;
        setBusyId(item.id);
        try {
            await waitlistApi.remove(item.id);
            toast.success("Removido da lista de espera");
            await load();
        } catch (e) { toast.error("Falha", { description: e.message }); }
        finally { setBusyId(null); }
    };
    const handleNotify = async (item) => {
        setBusyId(item.id);
        try {
            await waitlistApi.notify(item.id);
            toast.success("Notificação enviada", { description: `WhatsApp para ${item.clientName}` });
            await load();
        } catch (e) { toast.error("Falha", { description: e.message }); }
        finally { setBusyId(null); }
    };

    const handleDragStart = (item, ev) => {
        ev.dataTransfer.setData("application/x-kinng-waitlist", JSON.stringify(item.raw));
        ev.dataTransfer.effectAllowed = "move";
        onDragStart && onDragStart(item.raw);
    };

    return (
        <>
            <aside data-testid="waitlist-drawer"
                   className="w-full sm:w-[360px] shrink-0 border-l border-white/5 bg-slate-950/80 backdrop-blur-xl flex flex-col overflow-hidden">
                <header className="px-4 py-4 border-b border-white/5">
                    <div className="flex items-center justify-between">
                        <h3 className="salon-heading text-lg text-white">Lista de espera</h3>
                        {onClose && (
                            <button
                                type="button"
                                data-testid="waitlist-drawer-close"
                                onClick={onClose}
                                aria-label="Fechar"
                                className="h-8 w-8 rounded-lg border border-white/10 hover:border-white/30 text-slate-400 hover:text-white flex items-center justify-center transition-all shrink-0"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        )}
                    </div>
                    <div className="text-[11px] mt-1.5 flex items-center gap-2">
                        <span className="text-emerald-300 font-semibold">{matchedCount} com horário</span>
                        <span className="text-slate-600">·</span>
                        <span className="text-red-300 font-semibold">{queueCount} sem horário</span>
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-500">
                        <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-emerald-400"></span>Confirmar direto
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-red-400"></span>Definir horário
                        </span>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                    {loading && <div className="text-xs text-slate-500 text-center py-8">Carregando…</div>}
                    {!loading && items.length === 0 && (
                        <div className="text-center py-10 text-slate-500 text-xs">
                            <i className="fa-solid fa-inbox text-3xl mb-2 block text-slate-700"></i>
                            Nenhum agendamento aguardando
                        </div>
                    )}
                    {items.map((item) => {
                        const isMatch = item.type === "match";
                        const busy = busyId === item.id;
                        return (
                            <div
                                key={item.key}
                                data-testid={isMatch ? `waitlist-item-match-${item.id}` : `waitlist-item-nomatch-${item.id}`}
                                data-kind={item.type}
                                draggable={!isMatch}
                                onDragStart={!isMatch ? (ev) => handleDragStart(item, ev) : undefined}
                                onDragEnd={onDragEnd}
                                className={`relative rounded-xl border bg-slate-950/60 p-3 transition-all ${
                                    isMatch
                                        ? "border-emerald-400/60 ring-2 ring-emerald-300/60 shadow-[0_0_22px_-6px_rgba(16,185,129,0.5)]"
                                        : "border-red-400/50 ring-2 ring-red-300/45 shadow-[0_0_20px_-8px_rgba(239,68,68,0.45)] cursor-grab active:cursor-grabbing"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-white truncate">{item.clientName}</div>
                                        <div className="text-[11px] text-slate-400">{item.clientPhone || "—"}</div>
                                    </div>
                                    <span className={`shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${
                                        isMatch
                                            ? "border-emerald-400/50 text-emerald-200 bg-emerald-500/10"
                                            : "border-red-400/50 text-red-200 bg-red-500/10"
                                    }`}>
                                        <i className={`fa-solid ${isMatch ? "fa-calendar-check" : "fa-hourglass-half"}`}></i>
                                        {isMatch ? "Com horário" : "Sem horário"}
                                    </span>
                                </div>

                                <div className="mt-2 text-[11px] text-slate-300 flex items-center gap-1.5">
                                    <i className="fa-solid fa-scissors text-amber-300 text-[10px] shrink-0"></i>
                                    <span className="truncate">{item.serviceNames}</span>
                                    {!isMatch && (
                                        <>
                                            <span className="text-slate-600">·</span>
                                            <span className="shrink-0">{item.period}</span>
                                        </>
                                    )}
                                </div>

                                <div className={`mt-1 text-[11px] flex items-center gap-1.5 ${isMatch ? "text-emerald-200" : "text-red-200/90"}`}>
                                    <i className={`fa-solid ${isMatch ? "fa-clock" : "fa-circle-question"} text-[10px] shrink-0`}></i>
                                    <span className="truncate">
                                        {isMatch ? `Horário: ${item.whenLabel}` : item.whenLabel}
                                    </span>
                                </div>

                                {!isMatch && item.notified && item.remaining !== undefined && (
                                    <div className="mt-1 text-[10px] text-sky-300">
                                        <i className="fa-solid fa-hourglass-half text-[9px] mr-1"></i>
                                        {item.remaining} min p/ o cliente responder
                                    </div>
                                )}
                                {!isMatch && !item.notified && item.since && (
                                    <div className="mt-1 text-[10px] text-slate-500">Na fila há {item.since}</div>
                                )}

                                <div className="mt-3 pt-2 border-t border-white/5 flex gap-1.5">
                                    {isMatch ? (
                                        <>
                                            <button
                                                data-testid={`waitlist-confirm-${item.id}`}
                                                disabled={busy}
                                                onClick={() => handleConfirm(item)}
                                                className="flex-1 h-8 rounded-lg text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1 border border-emerald-400/60 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/35 active:bg-emerald-500/45 disabled:opacity-50 transition-colors"
                                            >
                                                <i className={`fa-solid ${busy ? "fa-spinner fa-spin" : "fa-check"}`}></i>Confirmar
                                            </button>
                                            <button
                                                data-testid={`waitlist-refuse-${item.id}`}
                                                disabled={busy}
                                                onClick={() => handleRefuseMatched(item)}
                                                className="flex-1 h-8 rounded-lg text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1 border border-red-400/55 bg-red-500/20 text-red-100 hover:bg-red-500/35 active:bg-red-500/45 disabled:opacity-50 transition-colors"
                                            >
                                                <i className="fa-solid fa-xmark"></i>Recusar
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                data-testid={`waitlist-define-${item.id}`}
                                                disabled={busy}
                                                onClick={() => onDefineTime?.(item.raw)}
                                                className="flex-1 h-8 rounded-lg text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1 border border-amber-400/55 bg-amber-500/15 text-amber-100 hover:bg-amber-500/30 active:bg-amber-500/40 disabled:opacity-50 transition-colors"
                                            >
                                                <i className="fa-solid fa-calendar-plus"></i>Definir horário
                                            </button>
                                            {!item.notified && (
                                                <button
                                                    data-testid={`waitlist-notify-${item.id}`}
                                                    disabled={busy}
                                                    onClick={() => handleNotify(item)}
                                                    title="Avisar o cliente que há vaga"
                                                    className="h-8 w-8 shrink-0 rounded-lg text-[11px] text-sky-200 border border-sky-500/40 hover:bg-sky-500/10 disabled:opacity-50"
                                                >
                                                    <i className={`fa-solid ${busy ? "fa-spinner fa-spin" : "fa-paper-plane"}`}></i>
                                                </button>
                                            )}
                                            <button
                                                data-testid={`waitlist-remove-${item.id}`}
                                                disabled={busy}
                                                onClick={() => handleRemoveQueue(item)}
                                                title="Cliente desistiu de esperar"
                                                className="h-8 w-8 shrink-0 rounded-lg text-[11px] text-red-300 border border-red-500/40 hover:bg-red-500/10 disabled:opacity-50"
                                            >
                                                <i className="fa-solid fa-xmark"></i>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="p-3 border-t border-white/5">
                    <button data-testid="waitlist-add-btn" onClick={() => setShowAdd(true)}
                            className="w-full h-9 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs uppercase tracking-wider">
                        <i className="fa-solid fa-plus mr-2"></i>Adicionar à fila
                    </button>
                </div>
            </aside>

            {showAdd && (
                <AddToWaitlistModal
                    clients={clients} services={services}
                    onClose={() => setShowAdd(false)}
                    onSaved={async () => { setShowAdd(false); await load(); }}
                />
            )}
        </>
    );
};

const AddToWaitlistModal = ({ clients, services, onClose, onSaved }) => {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [clientId, setClientId] = useState("");
    const [selectedSvc, setSelectedSvc] = useState([]);
    const [period, setPeriod] = useState("any");
    const [saving, setSaving] = useState(false);

    const pickClient = (c) => {
        setClientId(c.id); setName(c.name); setPhone(c.phone || "");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !phone.trim()) return toast.error("Nome e telefone são obrigatórios");
        setSaving(true);
        try {
            await waitlistApi.add({
                client_name: name.trim(), client_phone: phone.trim(),
                client_id: clientId || null, service_ids: selectedSvc,
                preferred_period: period, preferred_days: [], notes: "",
            });
            toast.success("Adicionado à fila!");
            onSaved();
        } catch (err) { toast.error("Falha", { description: err.message }); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}
                  data-testid="waitlist-add-modal"
                  className="w-full max-w-md rounded-3xl bg-slate-900 border border-white/10 p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="salon-heading text-lg text-white mb-4">Adicionar à lista de espera</h3>
                <div className="space-y-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Cliente existente</div>
                        <div className="max-h-28 overflow-y-auto rounded-lg border border-white/10 bg-slate-950/40 p-1 text-xs">
                            {clients.slice(0, 20).map((c) => (
                                <button type="button" key={c.id} onClick={() => pickClient(c)}
                                        className={`w-full text-left px-2 py-1 rounded ${clientId === c.id ? "bg-amber-500/15 text-amber-200" : "hover:bg-white/5 text-slate-300"}`}>
                                    {c.name} <span className="text-slate-500">· {c.phone}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)}
                               className="bg-slate-950/60 border border-white/10 rounded-lg px-2 py-2 text-xs text-white" />
                        <input placeholder="Telefone" value={phone} onChange={(e) => setPhone(e.target.value)}
                               className="bg-slate-950/60 border border-white/10 rounded-lg px-2 py-2 text-xs text-white" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Período preferido</div>
                        <div className="flex gap-1">
                            {["any", "manha", "tarde", "noite"].map((p) => (
                                <button type="button" key={p} onClick={() => setPeriod(p)}
                                        className={`flex-1 h-8 rounded-lg text-[10px] uppercase tracking-wider border ${period === p ? "bg-amber-500 text-slate-950 border-amber-400" : "border-white/10 text-slate-400"}`}>
                                    {PERIOD_LABEL[p]}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Serviços de interesse</div>
                        <div className="max-h-28 overflow-y-auto rounded-lg border border-white/10 bg-slate-950/40 p-1 text-xs">
                            {services.slice(0, 20).map((s) => {
                                const on = selectedSvc.includes(s.id);
                                return (
                                    <label key={s.id} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer ${on ? "bg-emerald-500/10" : "hover:bg-white/5"}`}>
                                        <input type="checkbox" checked={on}
                                               onChange={() => setSelectedSvc(on ? selectedSvc.filter((x) => x !== s.id) : [...selectedSvc, s.id])} />
                                        <span className="text-slate-200 flex-1">{s.name}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="mt-5 flex gap-2 justify-end">
                    <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg border border-white/10 text-slate-400 text-xs uppercase tracking-widest">Cancelar</button>
                    <button data-testid="waitlist-add-submit" type="submit" disabled={saving}
                            className="h-9 px-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs uppercase tracking-widest rounded-lg disabled:opacity-50">
                        {saving ? "Salvando..." : "Adicionar"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default WaitlistDrawer;
